import { notFound, type ParsedLocation } from '@tanstack/react-router';
import { getUmbracoDeliveryApiV2ContentExtendedItemPath } from '#/_generated/umbraco/delivery-api/features/content/content';
import type { IApiContentResponseModelBase, SiteSettingsUmbPageContentResponseModel } from '#/_generated/umbraco/delivery-api/models';
import { reportError } from '#/_packages/umbrella/features/error-handling/report-error';
import type { HttpError } from '#/_packages/umbrella/features/error-handling/types';
import { setPreviewCookie } from '#/_packages/umbrella/features/previews/utils/cookie-utils.ts';
import { isPreviewSearchEnabled } from '#/_packages/umbrella/features/previews/utils/request-preview-mode.ts';
import type { ContentRouteLoaderData } from '#/_packages/umbrella/features/routing/types/types';
import { preloadRendererModules } from '#/_packages/umbrella/integrations/umbraco/utils/renderer-modules';
import { getRequestDiagnostic } from '#/_packages/umbrella/lib/orval/request-error.ts';
import { formatRoute } from '#/_packages/umbrella/utils/route-utils';
import { baseMetaData, canonicalLinks, languageAlternateLinks, openGraphMetaData, structuredData, twitterMetaData } from '#/_packages/umbrella/utils/seo-utils';
import { getLocale } from '#/paraglide/runtime';
import { contentFailureStatus, contentRetryAfter, setContentResponseStatus } from './response-status';

const CONTENT_EXPAND =
    'properties[images,media,image,shareImage,contentArea[properties[images,media,image,slides[properties[image]]]],blockGrid[properties[images,media,image,slides[properties[image]]]]]';

export async function getContentRouteLoaderData(path: string | undefined, location: ParsedLocation): Promise<ContentRouteLoaderData> {
    const href = location.pathname;
    const search = location.search;

    try {
        if ('preview' in search && isPreviewSearchEnabled(search.preview)) {
            await setPreviewCookie();
        }

        const response = await getUmbracoDeliveryApiV2ContentExtendedItemPath({ expand: CONTENT_EXPAND }, formatRoute(path));

        if (!response.data.content) {
            return failedContentResponse(undefined, href, 502);
        }
        await preloadRendererModules(response.data.content);
        // Fetch before rendering so streamed Suspense does not hide the listing
        // behind a JavaScript-only reveal. This also applies to consuming clients.
        const eventPages =
            response.data.content.contentType === 'eventListingUmbPage' ? await (await import('#/_packages/umbrella/features/events/utils')).loadEventPages(response.data.content.id) : undefined;

        const newsPages =
            response.data.content.contentType === 'newsListingUmbPage'
                ? await (await import('#/_packages/umbrella/features/news/news-pages')).loadNewsPages(response.data.content.id, getCurrentContentCulture(response.data.content) ?? getLocale())
                : undefined;
        return {
            isError: false,
            content: response.data.content,
            ...(eventPages ? { eventPages } : {}),
            ...(newsPages ? { newsPages } : {}),
            breadCrumbs: response.data.breadCrumbs,
            loaderError: null,
        };
    } catch (error) {
        return failedContentResponse(error, href);
    }
}

export function getContentRouteHead(content: ContentRouteLoaderData['content'], siteSettings: SiteSettingsUmbPageContentResponseModel | undefined, siteOrigin: string, defaultCulture?: string) {
    if (!content) {
        return {
            links: [],
            meta: [{ name: 'robots', content: 'noindex, nofollow' }],
            scripts: [],
        };
    }
    return {
        links: [...canonicalLinks(content, siteOrigin), ...languageAlternateLinks(content, siteOrigin, defaultCulture)],
        meta: [...baseMetaData(content, siteSettings, siteOrigin), ...openGraphMetaData(content, siteSettings, siteOrigin), ...twitterMetaData(content, siteSettings)],
        scripts: [...structuredData(content, siteSettings, siteOrigin)],
    };
}

export function getCurrentContentCulture(content: IApiContentResponseModelBase): string | undefined {
    const currentPath = normalizePath(content.route.path);
    const cultureEntry = Object.entries(content.cultures).find(([, route]) => normalizePath(route.path) === currentPath);

    return cultureEntry?.[0];
}

function normalizePath(path: string | null | undefined) {
    if (!path) return '/';

    const normalized = path.startsWith('/') ? path : `/${path}`;
    return normalized.length > 1 ? normalized.replace(/\/+$/, '') : normalized;
}

async function failedContentResponse(error: unknown, location: string, fallbackStatus = 500): Promise<ContentRouteLoaderData> {
    const status = contentFailureStatus(error, fallbackStatus);
    const retryAfter = contentRetryAfter(error);
    if (retryAfter) await setContentResponseStatus(status, retryAfter);
    else await setContentResponseStatus(status);
    if (status === 404) throw notFound();
    const referenceId =
        status >= 500
            ? reportError(error ?? new Error('The CMS returned no content.'), {
                  operation: 'cms-content',
                  status,
              })
            : undefined;
    return {
        isError: true,
        content: undefined,
        breadCrumbs: undefined,
        loaderError: {
            type: 'http-error',
            status,
            referenceId,
            message: (import.meta.env.DEV ? getRequestDiagnostic(error) : undefined) ?? 'Content could not be loaded. Please try again.',
            location,
        } satisfies HttpError,
    };
}
